// A conversa do console com a API administrativa. Tudo por cookie de sessão — o token só aparece
// uma vez, na entrada, e nunca é guardado no navegador.
//
// NOTE: guardar o token em `localStorage` seria o caminho curto e o errado: qualquer script que entre
// na página passaria a poder cunhar licença. O cookie é `httpOnly`, então o JavaScript desta página
// não o alcança — nem o nosso.

import type { Venda } from "../domain/crm";

export class NaoAutorizado extends Error {
  constructor() {
    super("sessao expirada");
    this.name = "NaoAutorizado";
  }
}

async function chamar<T>(caminho: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/admin${caminho}`, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  });
  if (res.status === 401) throw new NaoAutorizado();
  const texto = await res.text();
  const corpo = texto ? JSON.parse(texto) : {};
  if (!res.ok)
    throw new Error(
      (corpo as { error?: string }).error ?? `erro ${res.status}`,
    );
  return corpo as T;
}

export interface Licenca {
  id: string;
  product: string;
  plan: string;
  tenant_limit: number | null;
  status: "active" | "past_due" | "canceled";
  // O que a licenca E, comercialmente. Nao se deduz: ver src/domain/crm.ts.
  sale: Venda;
  customer: string | null;
  // 🔴 Outra coisa que `customer`: aquele e o texto digitado por quem cunhou, este e a CONTA que
  // abre o painel do cliente. `null` = a licenca existe e ninguem a ve.
  customer_email: string | null;
  // Os dois vinculos com o Stripe. `null` nos dois = o pagamento nao chega nesta licenca.
  // 🔴 O customer e do CLIENTE (vem de `customers`, e vale para todas as licencas dele); a
  // assinatura e DESTA licenca. Um cliente com dois produtos tem um customer e duas assinaturas.
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
  installations: number;
  last_seen_at: string | null;
  // As versoes das instalacoes desta licenca, sem ordem garantida. Quem decide a mais nova e quem
  // esta atrasada e `src/version/semver.ts`, por numero — ver #22.
  versions: string[];
}

export interface Instalacao {
  id: string;
  product: string;
  revoked_at: string | null;
  created_at: string;
  last_seen_at: string | null;
  last_version: string | null;
  last_tenant_count: number | null;
  robot_name: string | null;
  credential_expires_at: string | null;
}

export type Situacao = "nova" | "adotar" | "migrar";

// A credencial descartável que baixa a imagem. 🔴 O segredo vem UMA vez, nesta resposta: o registro
// não o guarda em texto, e pedir outra adesão APAGA esta.
export interface CredencialDoRegistro {
  host: string;
  usuario: string;
  senha: string;
  imagem: string;
}

export interface Adesao {
  code: string;
  expires_at: string;
  product: string;
  // "script" = o hub entrega um instalador; "variaveis" = a instalacao ja existe e so precisa aderir.
  modo: "script" | "variaveis";
  // Nulos no modo que nao os usa. Nulo, e nao ausente, para a tela ter de decidir (#35).
  url: string | null;
  command: string | null;
  env: string | null;
  situacao: Situacao;
  registro: CredencialDoRegistro | null;
}

export const api = {
  entrar: (token: string) =>
    chamar<{ ok: true }>("/session", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  licencas: () =>
    chamar<{ licenses: Licenca[] }>("/licenses").then((r) => r.licenses),

  criarLicenca: (dados: {
    product: string;
    plan: string;
    tenant_limit: number | null;
    customer?: string;
    sale?: Venda;
  }) =>
    chamar<{ license: Licenca }>("/licenses", {
      method: "POST",
      body: JSON.stringify(dados),
    }),

  // `tenant_limit: null` é faixa ilimitada; a chave AUSENTE é "não mexer". São coisas diferentes, e
  // é por isso que o objeto é montado por quem chama em vez de levar um valor para cada campo.
  alterarLicenca: (
    id: string,
    dados: {
      plan?: string;
      tenant_limit?: number | null;
      customer?: string;
      sale?: Venda;
    },
  ) =>
    chamar<{ license: Licenca }>(`/licenses/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),

  instalacoes: (id: string) =>
    chamar<{ installations: Instalacao[] }>(
      `/licenses/${id}/installations`,
    ).then((r) => r.installations),

  gerarAdesao: (id: string, dados?: { locale?: string; situacao?: Situacao }) =>
    chamar<Adesao>(`/licenses/${id}/enrollments`, {
      method: "POST",
      body: JSON.stringify(dados ?? {}),
    }),

  // `days` não tem padrão de propósito: este token não tem renovação atrás dele, e o prazo é
  // decisão comercial. Um padrão escondido a tomaria por quem clica.
  emitirToken: (id: string, dados: { installation_id: string; days: number }) =>
    chamar<{
      token: string;
      expires_at: string;
      plan: string;
      tenant_limit: number | null;
    }>(`/licenses/${id}/tokens`, {
      method: "POST",
      body: JSON.stringify(dados),
    }),

  // ⚠️ Nao e edicao de texto: isto MOVE a licenca para o painel de outra pessoa, e o dono de antes
  // deixa de ver o que via. O e-mail e a chave das duas portas de entrada do painel.
  definirDono: (id: string, dados: { email: string; name?: string }) =>
    chamar<{ cliente: { id: string; name: string | null; email: string } }>(
      `/licenses/${id}/customer`,
      { method: "PUT", body: JSON.stringify(dados) },
    ),

  // 🔴 O `cus_` fica no CLIENTE, nao na licenca: colar aqui muda o vinculo de TODAS as licencas
  // daquela pessoa. A tela diz isso ao lado do campo, porque o endereco (dentro de uma licenca) faz
  // pensar o contrario.
  ligarClienteAoStripe: (id: string, stripe_customer_id: string) =>
    chamar<{ stripe_customer_id: string | null }>(
      `/licenses/${id}/stripe-customer`,
      { method: "PUT", body: JSON.stringify({ stripe_customer_id }) },
    ),

  ligarLicencaAoStripe: (id: string, stripe_subscription_id: string) =>
    chamar<{ stripe_subscription_id: string | null }>(
      `/licenses/${id}/stripe-subscription`,
      { method: "PUT", body: JSON.stringify({ stripe_subscription_id }) },
    ),

  cancelarLicenca: (id: string) =>
    chamar<{ canceled: string }>(`/licenses/${id}/cancel`, { method: "POST" }),

  revogarInstalacao: (id: string) =>
    chamar<{ revoked: string }>(`/installations/${id}/revoke`, {
      method: "POST",
    }),
};
